import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import type { Patient } from './types';

export function usePatients(searchTerm?: string) {
  const { clientId } = useCurrentClient();

  return useQuery({
    queryKey: ['patients', clientId, searchTerm ?? ''],
    enabled: !!clientId,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<Patient[]> => {
      if (!clientId) return [];
      let q = supabase.from('patients').select('*').eq('client_id', clientId);
      const term = searchTerm?.trim();
      if (term && term.length > 0) {
        const safe = term.replace(/%/g, '');
        q = q.or(`name.ilike.%${safe}%,cpf.ilike.%${safe}%,phone.ilike.%${safe}%`);
      }
      const { data, error } = await q.order('name', { ascending: true }).limit(500);
      if (error) throw error;
      return (data ?? []) as Patient[];
    },
  });
}

export interface CreatePatientInput {
  name: string;
  cpf?: string;
  phone?: string;
  email?: string;
  birthDate?: string;
  notes?: string;
}

export function useCreatePatient() {
  const qc = useQueryClient();
  const { clientId } = useCurrentClient();

  return useMutation({
    mutationFn: async (input: CreatePatientInput): Promise<Patient> => {
      if (!clientId) throw new Error('Cliente não selecionado');
      const { data, error } = await supabase
        .from('patients')
        .insert({
          client_id: clientId,
          name: input.name.trim(),
          cpf: input.cpf?.trim() || null,
          phone: input.phone?.trim() || null,
          email: input.email?.trim() || null,
          birth_date: input.birthDate || null,
          notes: input.notes?.trim() || null,
        })
        .select()
        .single();
      if (error) throw error;
      return data as Patient;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patients'] });
    },
    onError: (e: Error) => toast.error(`Erro ao criar paciente: ${e.message}`),
  });
}
